import { FC, useCallback, useEffect, useState } from 'react';

import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import {
  CardMedia,
  Checkbox,
  Link as MUILink,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';
import ProductVariant from '@/shared/types/products/productVariant.type';
import formatCurrency from '@/shared/utils/formatCurrency';

import getOptionValue from '../utils/getOptionValue';

type SearchBoxProductsOptionProps = {
  selected: boolean;
  variant: ProductVariant;
  externalFields?: Record<string, unknown>;
};

const SearchBoxProductsOption: FC<SearchBoxProductsOptionProps> = ({
  selected,
  variant,
  externalFields,
}) => {
  const [isNoPriceProvided, setIsNoPriceProvided] = useState(false);

  const getSKU = (variant: ProductVariant) => {
    const sku = variant.sku as string | undefined;

    if (sku) {
      return `SKU: ${sku}`;
    } else {
      return `Product ID: ${variant.product_id}`;
    }
  };

  const imageSize = {
    width: 70,
    height: 70,
  };

  const getPriceValue = useCallback(
    (variant: ProductVariant) => {
      const { product_prices, value } = variant;

      if (product_prices) {
        if (externalFields && 'currency' in externalFields) {
          const price = product_prices.find(
            (item) => item.currency_code === externalFields.currency,
          );

          if (price) {
            return formatCurrency(parseFloat(price.value), price.currency_code);
          } else {
            return `No price in ${externalFields.currency as string} (using 0.00)`;
          }
        } else {
          const price = product_prices.find(
            (item) =>
              item.currency_code === 'CAD' || item.currency_code === 'USD',
          );

          return price
            ? formatCurrency(parseFloat(price.value), price.currency_code)
            : formatCurrency(value);
        }
      }

      return formatCurrency(value, externalFields?.currency as string);
    },
    [externalFields],
  );

  useEffect(() => {
    if (getPriceValue(variant).startsWith('No price in')) {
      setIsNoPriceProvided(true);
    }
  }, [getPriceValue, variant]);

  return (
    <Stack
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `${imageSize.width}px 1fr auto`,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Checkbox
        checked={selected}
        sx={{
          visibility: 'hidden',
          position: 'absolute',
        }}
      />
      <CardMedia
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: imageSize.width,
          height: imageSize.height,
          borderRadius: 1,
          backgroundColor: 'grey.300',
        }}
        image={variant.image as string | undefined}
        title={variant.variant_name}
      >
        {!variant.image && (
          <InsertPhotoOutlinedIcon
            sx={{ width: 24, height: 24, color: 'white' }}
          />
        )}
      </CardMedia>

      <Stack>
        <MUILink
          component='a'
          target='_blank'
          underline='none'
          variant='body1'
          href={AppPath.Products + `/${variant.product_id}`}
          sx={{
            display: 'inline-flex',
            width: 'max-content',
            color: isNoPriceProvided ? 'warning.main' : 'primary.main',
          }}
        >
          {getOptionValue(variant)}
        </MUILink>

        <Typography variant='body2' color='text.secondary'>
          {getSKU(variant)}
        </Typography>

        <Typography variant='body2' color='text.secondary'>
          {`Variant ID: ${variant.variant_id.split('-')[0]}`}
        </Typography>
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ ml: 'auto' }}>
        {getPriceValue(variant)}
      </Typography>
    </Stack>
  );
};

export default SearchBoxProductsOption;
